import React from "react";
import _ from "lodash";
import {connect} from "react-redux";
import Grid from "@material-ui/core/Grid";
import {
  buildModelPortfolioTableData,
  calculatePortfolioInstrumentsAverageSrri,
  getTransactionItemSRRI,
} from "../../../../utils";
import {TRANSACTION_TYPE_VALUES, SERVICE_CONCEPTS} from "../../../../constants";
import {
  getAssetInternalId,
  paymentPlanHasPosition,
  portfolioIsPaymentPlansOnly
} from "../../../../../CustomerDashboard/utils";
import {
  extendAssetsWithDataAttribute,
  getRiskScoreConfirmation,
} from '../../../../../RiskProfiling/utils';
import {
  PAYMENT_PLANS_PORTFOLIO_ONLY_ADD_NEW_POSITION_MSG,
} from "../../../../../RiskProfiling/constants";
import {useConfirmationModalContext} from "../../../../../../components/ConfirmationModalContextProvider";
import {
  createInstrumentHandlers,
  createSwitchInHandler,
  getPortfolioInstruments,
  createHandleAddItems,
} from "../tradingOperations";
import {getErrorsWithout} from "../CombinedTrade/utils";
import { RiskScoreConfirmationModalContext } from "../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/InvestmentRecommendationStep";
import RiskScoreConfirmationModal
  from "../../../../../RiskProfiling/components/StepContent/components/step/ProductsSelectionSteps/components/InvestmentRecommendationStep/components/RiskScoreConfirmationModal/RiskScoreConfimrationModal";

// Hooks
import useTransactionHandlers from "./hooks/useTransactionHandlers";
import useNewVirtualPortfolio from "./hooks/useNewVirtualPortfolio";
import useTransactionSetup from "./hooks/useTransactionSetup";
import usePortfolioConfig from "./hooks/usePortfolioConfig";

// Components
import PortfolioHeader from "./PortfolioHeader";
import TransactionsTable from "./TransactionsTable";
import SwitchSection from "./components/SwitchSection";
import SwitchInstrumentModal from "./components/SwitchInstrumentModal";
import PortfolioSelectionModal from "./components/PortfolioSelectionModal";
import BuyInstrumentModal from "./components/BuyInstrumentModal";
import {portfolioHasTransactions} from "../CombinedTrade/dataUtils";
import InfoCard
  from "../../../../../RiskProfiling/components/StepContent/components/step/DynamicStep/components/InfoCard";
import useRiskModal from "../../../../hooks/useRiskModal";

import {useStyles} from "./styles"

export {buildTransactionDateSensitiveData, fetchAssetPrice} from "./utils";

const isSameAsset = (a, b) => getAssetInternalId(a) == getAssetInternalId(b);


const PortfolioTradeV2 = (props) => {
  const {
    portfolio,
    handlePortfolioChange,
    minDepositDate,
    customerId,
    dataService,
    isVirtual,
    isNewVirtual,
    showSectionsWithoutTransactions,
    showTradingActions,
    activeTradeOption: activeKey
  } = props;

  const classes = useStyles();

  const {
    isAnlageberatung,
    quotationFieldsSource,
    portfolioType,
    predefinedFilter,
  } = usePortfolioConfig({
    portfolio, isVirtual, isNewVirtual, showSectionsWithoutTransactions,
    allowedTradeOptions: props.allowedTradeOptions, dataService,
  });

  const transactions = portfolio.transactions;

  const handleChange = (item, data, isUpdatePortfolio = true) => {
    Object.keys(data).forEach(key => item[key] = data[key]);

    if (item.buy && !_.isEmpty(item.buy)) {
      item.buy.forEach(buyItem => transactionHandlers.handleTransactionValueChange(buyItem, buyItem.transaction_value, item, false));
    }

    if (isUpdatePortfolio) {
      handlePortfolioChange(portfolio, {
        transactions: {...portfolio.transactions},
        trading_errors: getErrorsWithout(portfolio, 'trading_errors', activeKey),
      });
    }
  };

  const {
    isWeightLoading,
    newVirtualRecalculateTransactions,
    handleNewVirtualPortfolioValueTotalChange,
    handleNewVirtualTransactionWeightChanged,
    handleNewVirtualTransactionWeightTypeChanged,
  } = useNewVirtualPortfolio({
    portfolio, isVirtual, isNewVirtual, handleChange, handlePortfolioChange
  });

  const instrumentHandlers = React.useMemo(
    () => createInstrumentHandlers(portfolio, dataService, isVirtual),
    [portfolio.depotType, dataService.hasRiskProfileConfigured, dataService.current_customer, isVirtual]
  );

  const transactionHandlers = useTransactionHandlers({
    portfolio, handleChange, handlePortfolioChange, isVirtual, isNewVirtual,
    newVirtualRecalculateTransactions, customerId, activeKey,
    resource: props.resource,
  });

  const clientSRI = dataService.srri;

  const {riskModalProps, openRiskReasoningQueue} = useRiskModal({
    handleRiskReasonChange: transactionHandlers.handleRiskReasonChange,
    isAnlageberatung,
    clientSRI,
    isModelportfolio: portfolio.isModelportfolio
  });

  const switchInstrumentHandler = React.useMemo(() => {
    return createSwitchInHandler(portfolio, dataService, isVirtual);
  }, [dataService.hasRiskProfileConfigured]);

  const instrumentsHandler = instrumentHandlers[activeKey];

  const getPortfolioInstrumentsFn = (key) => getPortfolioInstruments(portfolio, transactions, isVirtual, key);

  const modalContext = useConfirmationModalContext();

  const riskCheck = async (selectedAssets) => {
    const serviceConcept = dataService.serviceConcept || dataService._service_concept;
    const isAnlageberatungCheck = serviceConcept == SERVICE_CONCEPTS.Anlageberatung;
    const paymentPlanOnlyConfirmationRequired = isAnlageberatungCheck
      && portfolioIsPaymentPlansOnly(portfolio.data.components, portfolio.data.savings_plan)
      && _.some(selectedAssets.map((asset) => !paymentPlanHasPosition(asset, portfolio.data.savings_plan)));

    if (paymentPlanOnlyConfirmationRequired) {
      if (!await modalContext.confirm(PAYMENT_PLANS_PORTFOLIO_ONLY_ADD_NEW_POSITION_MSG, 'Bitte bestätigen Sie hier')) {
        return false;
      }
    } else {
      const riskScoreConfirmation = getRiskScoreConfirmation(
        dataService, extendAssetsWithDataAttribute(selectedAssets), true,
        getTransactionItemSRRI, _.get(selectedAssets, '0.isModelportfolio'), true);

      if (riskScoreConfirmation && !await modalContext.confirm(riskScoreConfirmation, 'Bitte bestätigen Sie hier')) {
        return false;
      }
    }

    return true;
  };

  const [openSelectionModal, setOpenSelectionModal] = React.useState(false);
  const [switchModalData, setSwitchModalData] = React.useState(undefined);

  const handleCloseTradingFlowModal = () => {
    setOpenSelectionModal(false);
  }
  const handleCloseSwitchModal = () => setSwitchModalData(undefined);

  const onModalClose = (isSwitch) => {
    isSwitch ? handleCloseSwitchModal() : handleCloseTradingFlowModal();
  }

  const handleAddItems = createHandleAddItems({
    portfolio, transactions, isVirtual,
    isPassiveDepot: _.get(portfolio, 'data.is_passive'),
    handleTransactionValueChange: transactionHandlers.handleTransactionValueChange,
    handleSwitchAmountEuroChanged: transactionHandlers.handleSwitchAmountEuroChanged,
    handleNewVirtualTransactionWeightTypeChanged,
    handleChange, handlePortfolioChange,
    riskCheck,
    customerId, dispatch: props.dispatch, resource: props.resource,
    modalContext, onModalClose, openRiskReasoningQueue,
  });

  const averageSrriValue = React.useMemo(() => {
    return !isVirtual && calculatePortfolioInstrumentsAverageSrri(portfolio);
  }, [JSON.stringify(portfolio.transactions)]);

  const {transactionsByProduct} = useTransactionSetup({
    portfolio, transactions, isVirtual, quotationFieldsSource,
    handleSellAllChanged: transactionHandlers.handleSellAllChanged,
  });

  const handleAddClick = () => {
    if (!portfolio.isModelportfolio) {
      setOpenSelectionModal(true);
      return;
    }
    const ptfTransactions = [buildModelPortfolioTableData(portfolio)];
    openRiskReasoningQueue(ptfTransactions)
    handleAddItems(activeKey)(ptfTransactions);
  };

  const handleRemoveClick = (item) => {
    const changes = {
      transactions: {...transactions},
      trading_errors: getErrorsWithout(portfolio, 'trading_errors', activeKey),
    };

    if(item.switchOutItemId){
      changes.transactions = {
        ...changes.transactions,
        [activeKey]: changes.transactions[activeKey].map(switchOutItem => {
          if(getAssetInternalId(switchOutItem.data) === item.switchOutItemId){
            return {
              ...switchOutItem,
              buy: switchOutItem.buy.filter(a => !isSameAsset(a.data, item.data))
            }
          } else {
            return switchOutItem;
          }
        })
      }
    } else {
      changes.transactions[activeKey] = changes.transactions[activeKey].filter(a => !isSameAsset(a.data, item.data))
    }

    if (!portfolioHasTransactions(changes, Object.keys(changes.transactions))){
      changes.trading_errors = getErrorsWithout(changes, 'trading_errors', 'portfolio')
    }

    handlePortfolioChange(portfolio, changes);
    // FIXME: conditional and combine all changes to prevent handlePortfolioChange call twice
    if(isNewVirtual) handleNewVirtualTransactionWeightTypeChanged(portfolio.transactionsWeightType);
  };

  return (
    <RiskScoreConfirmationModalContext.Provider
      value={{...riskModalProps, singleInvestmentEnabled: !dataService.isSellOnlyFlow}}
    >
      <Grid container spacing={2} data-id={'transactions-tables-container'}>
        <PortfolioHeader
          classes={classes}
          isAnlageberatung={isAnlageberatung}
          isVirtual={isVirtual}
          isNewVirtual={isNewVirtual}
          averageSrriValue={averageSrriValue}
          portfolio={portfolio}
          handleNewVirtualPortfolioValueTotalChange={handleNewVirtualPortfolioValueTotalChange}
        />

        {/* Buy / Sell table */}
        {activeKey !== TRANSACTION_TYPE_VALUES.SWITCH && (
          <TransactionsTable
            transactionKey={activeKey}
            transactions={transactions}
            portfolio={portfolio}
            classes={classes}
            isVirtual={isVirtual}
            isNewVirtual={isNewVirtual}
            isAnlageberatung={isAnlageberatung}
            isWeightLoading={isWeightLoading}
            portfolioType={portfolioType}
            quotationFieldsSource={quotationFieldsSource}
            transactionsByProduct={transactionsByProduct}
            minDepositDate={minDepositDate}
            showTradingActions={showTradingActions}
            handleTransactionValueChange={transactionHandlers.handleTransactionValueChange}
            handleTransactionTypeChange={transactionHandlers.handleTransactionTypeChange}
            handleDiscountChange={transactionHandlers.handleDiscountChange}
            handleNewVirtualTransactionWeightChanged={handleNewVirtualTransactionWeightChanged}
            handleNewVirtualTransactionWeightTypeChanged={handleNewVirtualTransactionWeightTypeChanged}
            handleRemoveClick={handleRemoveClick}
            handleSellAllChanged={transactionHandlers.handleSellAllChanged}
            handleSellAllQuantityChanged={transactionHandlers.handleSellAllQuantityChanged}
            handleOngoingFeeChange={transactionHandlers.handleOngoingFeeChange}
            handleEntryFeeChange={transactionHandlers.handleEntryFeeChange}
            handleBookingDateChange={transactionHandlers.handleBookingDateChange}
            handleEinzeltitelSettingChange={transactionHandlers.handleEinzeltitelSettingChange}
            handleLimitValueChange={transactionHandlers.handleLimitValueChange}
            handleQuantityChange={transactionHandlers.handleQuantityChange}
            handleAddClick={handleAddClick}
            showFunds={!instrumentsHandler.__disable_funds}
            einzeltitelEnabled={!instrumentsHandler.__only_funds}
            clientSRI={clientSRI}
          />
        )}

        {/* Switch section */}
        {activeKey === TRANSACTION_TYPE_VALUES.SWITCH && (
          <>
            {!isVirtual &&
              <Grid item xs={12}>
                <InfoCard
                  uid={"switch_info_text"}
                  info_text={
                    'Bitte beachten Sie, dass Anteile automatisch anhand der Anforderungen der Depotbanken gerundet werden.'
                  }
                  isNewDesign
                />
              </Grid>
            }
            <SwitchSection
              switchTransactions={transactions[activeKey] || []}
              classes={classes}
              isVirtual={isVirtual}
              isAnlageberatung={isAnlageberatung}
              portfolioType={portfolioType}
              portfolio={portfolio}
              activeKey={activeKey}
              minDepositDate={minDepositDate}
              quotationFieldsSource={quotationFieldsSource}
              transactionsByProduct={transactionsByProduct}
              showTradingActions={showTradingActions}
              handleTransactionValueChange={transactionHandlers.handleTransactionValueChange}
              handleSwitchAmountEuroChanged={transactionHandlers.handleSwitchAmountEuroChanged}
              handleSwitchPercentageChanged={transactionHandlers.handleSwitchPercentageChanged}
              handleTransactionTypeChange={transactionHandlers.handleTransactionTypeChange}
              handleDepositDateChange={transactionHandlers.handleDepositDateChange}
              handleRemoveClick={handleRemoveClick}
              handleSellAllChanged={transactionHandlers.handleSellAllChanged}
              handleDiscountChange={transactionHandlers.handleDiscountChange}
              handleDistributeQuantitiesClick={transactionHandlers.handleDistributeQuantitiesClick}
              setSwitchModalData={setSwitchModalData}
              dataService={dataService}
              handleAddClick={handleAddClick}
            />

            {!!switchModalData && (
              <SwitchInstrumentModal
                switchModalData={switchModalData}
                handleClose={handleCloseSwitchModal}
                portfolio={portfolio}
                transactions={transactions}
                switchInstrumentHandler={switchInstrumentHandler}
                handleAddItems={handleAddItems}
                predefinedFilter={predefinedFilter}
                customerId={customerId}
                isCustomerApp={props.isCustomerApp}
                isVirtual={isVirtual}
              />
            )}
          </>
        )}

        {/* Modals */}

        {!!openSelectionModal && activeKey === TRANSACTION_TYPE_VALUES.BUY && (
          <BuyInstrumentModal
            onClose={handleCloseTradingFlowModal}
            portfolio={portfolio}
            transactions={transactions[activeKey]}
            instrumentsHandler={instrumentsHandler}
            handleAddItems={handleAddItems(activeKey)}
            predefinedFilter={predefinedFilter}
            customerId={customerId}
            isCustomerApp={props.isCustomerApp}
            isVirtual={isVirtual}
            portfolioInstruments={getPortfolioInstrumentsFn(activeKey)}
          />
        )}

        {!!openSelectionModal && [TRANSACTION_TYPE_VALUES.SELL, TRANSACTION_TYPE_VALUES.SWITCH].includes(activeKey) && (
          <PortfolioSelectionModal
            onClose={handleCloseTradingFlowModal}
            portfolioInstruments={getPortfolioInstrumentsFn(activeKey)}
            instrumentsHandler={instrumentsHandler}
            onSubmit={handleAddItems(activeKey)}
            action={activeKey}
            withSrri={isAnlageberatung}
            clientSRRI={clientSRI}
          />
        )}
      </Grid>
      <RiskScoreConfirmationModal />
    </RiskScoreConfirmationModalContext.Provider>
  );
};

PortfolioTradeV2.defaultProps = {
  showSectionsWithoutTransactions: true,
  showTradingActions: true
};

export default connect()(PortfolioTradeV2);
